import { useEffect, useRef } from "react";

export interface UseCtaViewportImpressionOptions {
    enabled: boolean;
    /** Fires once when CTA intersects viewport (non-blocking). */
    onImpression: () => void;
    /** Minimum visible ratio (0–1). */
    threshold?: number;
}

/**
 * One-shot viewport visibility for CTR / impression analytics.
 * Safe when `IntersectionObserver` is missing (fires `onImpression` once via microtask).
 */
export function useCtaViewportImpression(
    element: Element | null,
    { enabled, onImpression, threshold = 0.2 }: UseCtaViewportImpressionOptions
): void {
    const fired = useRef(false);
    const onImpressionRef = useRef(onImpression);
    onImpressionRef.current = onImpression;

    useEffect(() => {
        fired.current = false;
    }, [element]);

    useEffect(() => {
        if (!enabled || fired.current || !element) return;

        const fire = () => {
            if (fired.current) return;
            fired.current = true;
            queueMicrotask(() => {
                try {
                    onImpressionRef.current();
                } catch {
                    /* never break UI */
                }
            });
        };

        if (typeof IntersectionObserver === "undefined") {
            fire();
            return;
        }

        const io = new IntersectionObserver(
            (entries) => {
                const visible = entries.some(
                    (e) => e.isIntersecting && e.intersectionRatio >= threshold
                );
                if (visible) {
                    fire();
                    io.disconnect();
                }
            },
            { threshold: [0, threshold, 0.5, 1], rootMargin: "24px 0px" }
        );

        try {
            io.observe(element);
        } catch {
            fire();
            return;
        }

        return () => {
            try {
                io.disconnect();
            } catch {
                /* noop */
            }
        };
    }, [element, enabled, threshold]);
}
